import { HugeiconsIcon } from "@hugeicons/react";
import { Show } from "shared/components";
import { HighlightText } from "shared/components/HighlightText";
import { cx } from "shared/utils";
import type { GlobalSearchConversationItem } from "views/inbox/types";
import {
  CONTACT_TYPE_LABELS,
  CONVERSATIONS_ICON,
  CONVERSATIONS_LABEL,
  CONVERSATION_CHANNELS
} from "./constants";
import { formatConversationTotal, formatSentAt } from "./utils";

export interface ConversationResults {
  items: GlobalSearchConversationItem[];
  total: number;
  capped: boolean;
}

interface ConversationGroupProps {
  query: string;
  results?: ConversationResults;
  startIndex: number;
  activeIndex: number;
  onSelect: (item: GlobalSearchConversationItem) => void;
  onSeeAll: () => void;
  onHover: (index: number) => void;
}

const contactSubtitle = (item: GlobalSearchConversationItem) =>
  [
    item.contact?.contact_type
      ? CONTACT_TYPE_LABELS[item.contact.contact_type]
      : null,
    item.contact?.home
  ]
    .filter(Boolean)
    .join(" · ");

const ConversationRow = ({
  item,
  query,
  active,
  onSelect,
  onHover
}: {
  item: GlobalSearchConversationItem;
  query: string;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
}) => {
  const channel = CONVERSATION_CHANNELS[item.channel];
  const subtitle = contactSubtitle(item);

  return (
    <div
      role="option"
      aria-selected={active}
      data-active={active}
      onClick={onSelect}
      onMouseEnter={onHover}
      className={cx(
        "relative flex w-full cursor-pointer items-start gap-3 px-4 py-2.5 text-left transition-all",
        active ? "bg-alpha-40" : ""
      )}
    >
      <Show if={active}>
        <span className="absolute inset-y-1 left-0 w-0.5 rounded-r bg-primary-500" />
      </Show>
      <span className="min-w-0 grow">
        <span className="flex items-center gap-1.5">
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
            {item.contact?.display_name || "Unknown contact"}
          </span>
          <span className="flex shrink-0 items-center gap-1 rounded-full border border-alpha-100 bg-alpha-20 px-1.5 text-[10px] font-medium text-content-300">
            <HugeiconsIcon
              icon={channel?.icon ?? CONVERSATIONS_ICON}
              size={11}
              className="stroke-content-300"
              aria-hidden
            />
            {channel?.label ?? item.channel}
          </span>
        </span>
        <Show if={subtitle}>
          <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-200">
            {subtitle}
          </span>
        </Show>
        <Show if={item.subject}>
          <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium text-content-500">
            <HighlightText text={item.subject ?? ""} query={query} />
          </span>
        </Show>
        <span className="mt-0.5 line-clamp-2 text-xs text-content-500">
          <HighlightText text={item.snippet} query={query} />
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-0.5 text-[11px] text-content-200">
        <span>{formatSentAt(item.sent_at)}</span>
        <Show if={item.match_count > 1}>
          <span>{item.match_count} messages</span>
        </Show>
      </span>
    </div>
  );
};

export const ConversationGroup = ({
  query,
  results,
  startIndex,
  activeIndex,
  onSelect,
  onSeeAll,
  onHover
}: ConversationGroupProps) => {
  if (results && results.items.length === 0) return null;

  const totalLabel = results
    ? formatConversationTotal(results.total, results.capped)
    : "";
  const hasMore = results
    ? results.capped || results.total > results.items.length
    : false;

  return (
    <div role="group" aria-label={CONVERSATIONS_LABEL} className="py-1">
      <div className="flex items-center gap-2 px-4 py-1.5">
        <HugeiconsIcon
          icon={CONVERSATIONS_ICON}
          size={15}
          className="shrink-0 stroke-content-200"
          aria-hidden
        />
        <span className="text-xs font-medium uppercase tracking-wide text-content-200">
          {CONVERSATIONS_LABEL}
        </span>
        <Show if={results}>
          <span className="rounded-full bg-alpha-40 px-1.5 text-[10px] font-medium text-content-200">
            {hasMore ? `${results?.items.length} of ${totalLabel}` : totalLabel}
          </span>
        </Show>
      </div>
      {results ? (
        <>
          {results.items.map((item, offset) => {
            const index = startIndex + offset;
            return (
              <ConversationRow
                key={item.id}
                item={item}
                query={query}
                active={index === activeIndex}
                onSelect={() => onSelect(item)}
                onHover={() => onHover(index)}
              />
            );
          })}
          <Show if={hasMore}>
            <button
              type="button"
              onClick={onSeeAll}
              className="px-4 py-2 text-xs font-medium text-primary-500 hover:underline"
            >
              See all {totalLabel} conversations in the Inbox
            </button>
          </Show>
        </>
      ) : (
        <p className="px-4 py-2.5 text-xs text-content-200">
          Searching messages…
        </p>
      )}
    </div>
  );
};
